'use client';

import { useState } from 'react';
import AccordionItem from '@modules/common/components/accordion-item';
import FilterTabs from '@modules/common/components/filter-tabs';
import { HOMEPAGE_FAQ_ITEMS, FAQ_CATEGORIES } from '@constants/homepage';
import type { FAQItem } from '@constants/homepage';

interface FAQSectionProps {
  /** Optional FAQ items (defaults to homepage FAQ items) */
  faqItems?: FAQItem[];
  /** Optional FAQ category filters (defaults to homepage categories) */
  faqCategories?: { id: string; label: string }[];
  /** Override the default section className (default: 'py-5') */
  className?: string;
}

export default function FAQSection({ faqItems, faqCategories, className }: FAQSectionProps) {
  const items = faqItems ?? HOMEPAGE_FAQ_ITEMS;
  const categories = faqCategories ?? FAQ_CATEGORIES;

  const [openId, setOpenId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id ?? 'shipping');

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
    setOpenId(null);
  };

  const filteredFAQs = items.filter(
    (item) => item.category === activeCategory
  );

  return (
    <section
      className={className ?? 'py-5'}
      aria-labelledby="faq-heading"
    >
      <div className="max-w-3xl mx-auto px-4">
        <h2
          id="faq-heading"
          className="text-[20px] md:text-[36px] font-black text-left md:text-center text-brand-navy mb-4 md:mb-8"
        >
          Frequently asked questions
        </h2>

        {/* Filter Tabs */}
        <div className="md:flex md:justify-center">
          <FilterTabs
            filters={categories}
            activeFilter={activeCategory}
            onFilterChange={handleCategoryChange}
            ariaLabel="FAQ categories"
          />
        </div>

        <div className="divide-y divide-gray-200">
          {filteredFAQs.map((item) => (
            <AccordionItem
              key={item.id}
              id={`faq-${item.id}`}
              title={item.question}
              isOpen={openId === item.id}
              onToggle={() => setOpenId((prev) => (prev === item.id ? null : item.id))}
            >
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                {item.answer}
              </p>
            </AccordionItem>
          ))}
        </div>
      </div>
    </section>
  );
}
